import React from 'react';
import {
  GitBranch,
  ExternalLink,
  ArrowRight,
  Users,
  Eye,
  ShieldAlert,
  ChevronDown,
} from 'lucide-react';
import CommitAddDelDonut from '../../components/repository/CommitAddDelDonut';
import ContributorRowAvatar from '../../components/contributors/ContributorRowAvatar';
import PrStatusIcon from '../../components/repository/PrStatusIcon';
import { Badge } from '../../components/ui/Badge';
import {
  commitHashClass,
  prTitleClass,
  prMetaClass,
} from '../../components/repository/repoViewTheme';
import {
  formatRepoTabDate,
  commitSearchBlob,
  activitySearchBlob,
  prSearchBlob,
  commitGitHubUrl,
  commitBranchGitHubUrl,
  githubRepoBranchUrl,
  formatFilesChanged,
  commitAuthorDisplay,
  commitChurnRatio,
} from './repositoryDetailUtils';

function sensitiveConfidenceBadgeRing(confidence) {
  const c = String(confidence || '').toLowerCase();
  if (c === 'high') {
    return 'border-red-800/70 bg-red-950/45 text-red-100 ring-red-900/50';
  }
  if (c === 'medium') {
    return 'border-amber-700/60 bg-amber-950/35 text-amber-100 ring-amber-900/45';
  }
  return 'border-dark-border/80 bg-dark-elevated text-slate-200 ring-slate-400';
}

function normalisePrLabels(raw) {
  if (!Array.isArray(raw)) return [];
  const out: string[] = [];
  for (const item of raw) {
    if (item == null) continue;
    if (typeof item === 'string') {
      const t = item.trim();
      if (t) out.push(t);
    } else if (typeof item === 'object' && item.name != null) {
      const t = String(item.name).trim();
      if (t) out.push(t);
    }
  }
  return out;
}

function makeDonutColumn(idPrefix: string) {
  return {
    id: `${idPrefix}Donut`,
    header: 'Diff',
    enableGlobalFilter: false,
    enableSorting: false,
    accessorFn: (row) => (Number(row.additions) || 0) + (Number(row.deletions) || 0),
    meta: {
      tdClassName:
        'px-3 py-3 text-sm align-middle text-center w-[3.75rem] min-w-[3.75rem] whitespace-nowrap',
    },
    cell: ({ row }) => (
      <CommitAddDelDonut additions={row.original.additions} deletions={row.original.deletions} />
    ),
  };
}

function makeChurnColumn(idPrefix: string) {
  return {
    id: `${idPrefix}Churn`,
    header: 'Churn',
    enableGlobalFilter: false,
    accessorFn: (row) => {
      const ratio = commitChurnRatio(row.additions, row.deletions);
      return ratio == null ? -1 : ratio;
    },
    meta: {
      tdClassName:
        'px-4 py-3 text-sm align-middle text-center w-[8.5rem] min-w-[8.5rem] whitespace-nowrap',
    },
    cell: ({ row }) => {
      const r = row.original;
      const a = Number(r.additions) || 0;
      const d = Number(r.deletions) || 0;
      const ratio = commitChurnRatio(r.additions, r.deletions);
      const addDelLine = (
        <span className="text-xs font-medium tabular-nums tracking-tight">
          <span style={{ color: 'rgb(34 197 94)' }}>+{a}</span>
          <span className="px-0.5 font-bold text-slate-100">|</span>
          <span style={{ color: 'rgb(239 68 68)' }}>-{d}</span>
        </span>
      );
      if (ratio == null) {
        return <span className="text-xs tabular-nums text-slate-500">—</span>;
      }
      return (
        <div className="flex flex-col items-center justify-center gap-0.5 leading-tight text-center">
          <div className="flex flex-row flex-wrap items-baseline justify-center gap-x-1.5">
            <span className="text-xs font-medium tabular-nums text-slate-200">
              {`${Math.round(ratio * 100)}%`}
            </span>
            <span className="text-xs font-medium tabular-nums text-slate-200">Churn</span>
          </div>
          {addDelLine}
        </div>
      );
    },
  };
}

/** Column definitions use empty deps in the original page; safe as module-level constants. */
export const repositoryBranchesColumns = [
  {
    id: 'branchFilters',
    accessorFn: () => '',
    enableSorting: false,
    enableGlobalFilter: false,
    filterFn: (row, _columnId, filter) => {
      if (!filter || (!filter.defaultOnly && !filter.protectedOnly && !filter.staleOnly)) {
        return true;
      }
      const r = row.original;
      if (filter.defaultOnly && !r.isDefault) return false;
      if (filter.protectedOnly && !r.isProtected) return false;
      if (filter.staleOnly && !r.stale) return false;
      return true;
    },
    meta: { filterOnly: true },
  },
  {
    accessorKey: 'name',
    header: 'Branch',
    cell: ({ row }) => {
      const isDefault = row.original.isDefault;
      return (
        <div
          className={`flex items-center gap-2 min-w-0 ${
            isDefault
              ? 'rounded-lg border border-brand-purple/30 bg-brand-purple/10 px-2 py-1 -mx-2'
              : ''
          }`}
        >
          <GitBranch className="w-4 h-4 text-slate-500 flex-shrink-0" aria-hidden />
          <span
            className={`font-medium truncate ${isDefault ? 'text-slate-100' : 'text-slate-200'}`}
          >
            {row.original.name}
          </span>
        </div>
      );
    },
  },
  {
    id: 'isDefault',
    accessorKey: 'isDefault',
    header: 'Default',
    cell: ({ getValue }) =>
      getValue() ? <Badge variant="purple">Yes</Badge> : <span className="text-slate-500">—</span>,
  },
  {
    id: 'isProtected',
    accessorKey: 'isProtected',
    header: 'Protected',
    cell: ({ getValue }) =>
      getValue() ? <Badge variant="default">Yes</Badge> : <span className="text-slate-500">—</span>,
  },
  {
    accessorKey: 'stale',
    header: 'Stale',
    cell: ({ getValue }) =>
      getValue() ? <Badge variant="warning">Yes</Badge> : <span className="text-slate-500">—</span>,
  },
  {
    id: 'storedCommits',
    accessorFn: (row) => (row.isCommitSyncBranch ? row.commits : null),
    header: 'Stored commits',
    cell: ({ row }) => {
      const r = row.original;
      if (r.isCommitSyncBranch) {
        return r.commits != null ? String(r.commits) : '—';
      }
      const refs = (r.commitSyncBranches || []).join(', ') || 'configured deployment branches';
      return (
        <span
          className="text-slate-500"
          title={`VantagePoint only stores commit history for deployment branches (${refs}). Other branches are listed from GitHub without per-branch commit rows.`}
        >
          —
        </span>
      );
    },
  },
  {
    id: 'lastCommitAuthor',
    accessorFn: (row) => row.lastCommitAuthor || '',
    header: 'Last author',
    cell: ({ row }) => {
      const r = row.original;
      const label = r.lastCommitAuthor || '—';
      if (!label || label === '—') {
        return <span className="text-slate-500">—</span>;
      }
      return (
        <span className="inline-flex max-w-full min-w-0 items-center gap-1.5">
          <ContributorRowAvatar
            row={{ displayName: label, authorGithub: r.lastCommitAuthorGithub }}
            size="xxs"
            className="shrink-0"
          />
          <span className="min-w-0 truncate">{label}</span>
        </span>
      );
    },
  },
  {
    id: 'lastCommitAt',
    accessorFn: (row) => (row.lastCommitDate ? new Date(row.lastCommitDate).getTime() : 0),
    header: 'Last commit',
    cell: ({ row }) => formatRepoTabDate(row.original.lastCommitDate),
  },
];

export const repositoryCommitsColumns = [
  {
    id: 'commitSearch',
    accessorFn: (row) => commitSearchBlob(row),
    header: () => null,
    enableSorting: false,
    enableGlobalFilter: true,
    meta: { filterOnly: true },
  },
  {
    id: 'commitFiles',
    accessorFn: (row) => {
      const n = Number(row.changedFiles);
      return Number.isFinite(n) && n >= 0 ? n : 0;
    },
    header: () => null,
    enableSorting: true,
    enableGlobalFilter: false,
    meta: { filterOnly: true },
  },
  {
    id: 'commitAdditions',
    accessorFn: (row) => {
      const n = Number(row.additions);
      return Number.isFinite(n) && n >= 0 ? n : 0;
    },
    header: () => null,
    enableSorting: true,
    enableGlobalFilter: false,
    meta: { filterOnly: true },
  },
  {
    id: 'commitDeletions',
    accessorFn: (row) => {
      const n = Number(row.deletions);
      return Number.isFinite(n) && n >= 0 ? n : 0;
    },
    header: () => null,
    enableSorting: true,
    enableGlobalFilter: false,
    meta: { filterOnly: true },
  },
  {
    id: 'commit',
    header: 'Commit',
    enableGlobalFilter: false,
    accessorFn: (row) => (row.authorDate ? new Date(row.authorDate).getTime() : 0),
    meta: {
      tdClassName:
        'px-4 py-3 text-sm text-slate-200 max-w-xl min-w-[12rem] whitespace-normal align-top',
    },
    cell: ({ row }) => {
      const r = row.original;
      const firstLine = (r.message || '').split('\n')[0] || '—';
      const author = commitAuthorDisplay(r);
      const dateStr = formatRepoTabDate(r.authorDate);
      const branchRaw = r.branch != null ? String(r.branch).trim() : '';
      const branchLabel = branchRaw || '—';
      const files = formatFilesChanged(r.changedFiles);
      const sha = r.sha && String(r.sha).trim();
      const shortSha = sha && sha.length >= 7 ? sha.slice(0, 7) : sha || '—';
      const commitUrl = commitGitHubUrl(r);
      const branchUrl = commitBranchGitHubUrl(r);
      const hashLinkClass = `${commitHashClass} hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple/50`;
      const branchRowClass =
        'inline-flex min-w-0 max-w-[11rem] items-center gap-1.5 text-xs text-slate-300';
      const branchLinkRowClass = `${branchRowClass} rounded-md hover:text-brand-teal-light focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple/50`;

      const branchInner = (
        <>
          <GitBranch className="h-3.5 w-3.5 shrink-0 text-slate-500" aria-hidden />
          <span className="min-w-0 truncate font-mono tabular-nums">{branchLabel}</span>
        </>
      );

      return (
        <div className="flex gap-3 items-start min-w-0">
          <div className="flex flex-col gap-1.5 shrink-0 items-stretch">
            {commitUrl ? (
              <a
                href={commitUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={hashLinkClass}
                aria-label={`View commit ${sha} on GitHub`}
              >
                {shortSha}
              </a>
            ) : (
              <span
                className={commitHashClass}
                title={sha ? 'No GitHub link for this commit' : undefined}
              >
                {shortSha}
              </span>
            )}
            {branchUrl ? (
              <a
                href={branchUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={branchLinkRowClass}
                title={branchRaw}
                aria-label={`View branch ${branchRaw} on GitHub`}
              >
                {branchInner}
              </a>
            ) : (
              <span
                className={branchRowClass}
                title={branchRaw ? 'No GitHub link for this branch' : undefined}
              >
                {branchInner}
              </span>
            )}
          </div>
          <div className="space-y-1.5 min-w-0 flex-1">
            <div className="font-medium text-slate-100 leading-snug" title={r.message || undefined}>
              {firstLine}
            </div>
            <div className={`${prMetaClass} flex flex-wrap gap-x-2 gap-y-0.5 items-center`}>
              <span className="inline-flex min-w-0 max-w-full items-center gap-1.5">
                <ContributorRowAvatar
                  row={{ ...r, displayName: author }}
                  size="xxs"
                  className="shrink-0"
                />
                <span className="min-w-0 truncate">{author}</span>
              </span>
              <span className="text-slate-600" aria-hidden>
                ·
              </span>
              <span>{dateStr}</span>
              <span className="text-slate-600" aria-hidden>
                ·
              </span>
              <span>{files}</span>
              {r.sensitiveDataDetection ? (
                <>
                  <span className="text-slate-600" aria-hidden>
                    ·
                  </span>
                  <span
                    className={`inline-flex shrink-0 items-center gap-0.5 rounded-full border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1 ${sensitiveConfidenceBadgeRing(
                      r.sensitiveDataDetection.confidence
                    )}`}
                    title={[
                      `Patterns: ${r.sensitiveDataDetection.patterns.join(', ')}`,
                      `Confidence: ${r.sensitiveDataDetection.confidence || '—'}`,
                      r.sensitiveDataDetection.detectedAt
                        ? `Detected: ${formatRepoTabDate(r.sensitiveDataDetection.detectedAt)}`
                        : '',
                    ]
                      .filter(Boolean)
                      .join('\n')}
                    aria-label={`Sensitive data: ${r.sensitiveDataDetection.patterns.join(', ')}. Confidence ${
                      r.sensitiveDataDetection.confidence || 'unknown'
                    }.`}
                  >
                    <ShieldAlert className="h-3 w-3 shrink-0" aria-hidden />
                    <span className="max-w-[4.5rem] truncate capitalize">
                      {r.sensitiveDataDetection.confidence || 'scan'}
                    </span>
                  </span>
                </>
              ) : null}
            </div>
          </div>
        </div>
      );
    },
  },
  makeDonutColumn('commit'),
  makeChurnColumn('addDel'),
];

export const repositoryActivityColumns = [
  {
    id: 'activitySearch',
    accessorFn: (row) => activitySearchBlob(row),
    header: () => null,
    enableSorting: false,
    enableGlobalFilter: true,
    meta: { filterOnly: true },
  },
  {
    id: 'activityType',
    accessorFn: () => '',
    enableSorting: false,
    enableGlobalFilter: false,
    filterFn: (row, _columnId, val) =>
      !val ||
      val === 'all' ||
      String(row.original.type || '').toLowerCase() === String(val).toLowerCase(),
    meta: { filterOnly: true },
  },
  {
    id: 'activity',
    header: 'Activity',
    enableGlobalFilter: false,
    accessorFn: (row) => (row.createdAt ? new Date(row.createdAt).getTime() : 0),
    meta: {
      tdClassName:
        'px-4 py-3 text-sm text-slate-200 max-w-xl min-w-[12rem] whitespace-normal align-top',
    },
    cell: ({ row }) => {
      const r = row.original;
      const typeLabel = (r.type && String(r.type).trim()) || 'Activity';
      const titleLine = r.title ? String(r.title).trim() : '';
      const actor = r.actorFullName || r.actor || 'Unknown';
      const dateStr = formatRepoTabDate(r.createdAt);
      const descRaw = r.description != null ? String(r.description).trim() : '';
      const descLong = descRaw.length > 160;
      const actorUrlTrim = r.actorUrl != null ? String(r.actorUrl).trim() : '';
      const profileHref =
        actorUrlTrim && /^https?:\/\//i.test(actorUrlTrim)
          ? actorUrlTrim
          : r.actor && String(r.actor).trim()
            ? `https://github.com/${encodeURIComponent(String(r.actor).trim())}`
            : null;
      const typeBadgeClass =
        'inline-flex shrink-0 items-center justify-center rounded-lg px-2 py-0.5 text-xs font-medium uppercase tracking-wide ' +
        'bg-dark-elevated text-slate-200 ring-slate-400';
      const actorInner = (
        <>
          <ContributorRowAvatar
            row={{ ...r, displayName: actor }}
            size="xxs"
            className="shrink-0"
          />
          <span className="min-w-0 truncate">{actor}</span>
        </>
      );
      return (
        <div className="flex gap-3 items-start min-w-0">
          <div className="shrink-0 pt-0.5">
            <span className={typeBadgeClass}>{typeLabel}</span>
          </div>
          <div className="space-y-1.5 min-w-0 flex-1">
            <div className="font-medium text-slate-100 leading-snug" title={titleLine || undefined}>
              {titleLine || typeLabel}
            </div>
            {descRaw ? (
              descLong ? (
                <details className="min-w-0 group">
                  <summary className="cursor-pointer list-none text-xs font-medium text-vivid-red hover:underline [&::-webkit-details-marker]:hidden">
                    <span className="inline-flex items-center gap-1">
                      Activity details
                      <ChevronDown
                        className="h-3.5 w-3.5 shrink-0 transition-transform group-open:rotate-180"
                        aria-hidden
                      />
                    </span>
                  </summary>
                  <p className="mt-1.5 text-xs leading-snug text-slate-400 whitespace-pre-wrap break-words border-l-2 border-dark-border pl-2">
                    {descRaw}
                  </p>
                </details>
              ) : (
                <p
                  className="line-clamp-3 text-xs leading-snug text-slate-400 break-words"
                  title={descRaw}
                >
                  {descRaw}
                </p>
              )
            ) : null}
            <div className="text-xs text-slate-400 flex flex-wrap gap-x-2 gap-y-0.5 items-center">
              {profileHref ? (
                <a
                  href={profileHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex min-w-0 max-w-full items-center gap-1.5 rounded-sm text-inherit hover:text-red-400 focus:outline-none focus-visible:ring-offset-dark-elevated"
                  aria-label={`${actor} on GitHub (opens in new tab)`}
                >
                  {actorInner}
                </a>
              ) : (
                <span className="inline-flex min-w-0 max-w-full items-center gap-1.5">
                  {actorInner}
                </span>
              )}
              <span className="text-slate-600" aria-hidden>
                ·
              </span>
              <span>{dateStr}</span>
            </div>
          </div>
        </div>
      );
    },
  },
];

export const repositoryPullRequestsColumns = [
  {
    id: 'prSearch',
    accessorFn: (row) => prSearchBlob(row),
    header: () => null,
    enableSorting: false,
    enableGlobalFilter: true,
    meta: { filterOnly: true },
  },
  {
    id: 'prState',
    accessorFn: () => '',
    enableSorting: false,
    enableGlobalFilter: false,
    filterFn: (row, _columnId, val) =>
      !val ||
      val === 'all' ||
      String(row.original.state || 'open').toLowerCase() === String(val).toLowerCase(),
    meta: { filterOnly: true },
  },
  {
    id: 'prFiles',
    accessorFn: (row) => {
      const n = Number(row.changedFiles);
      return Number.isFinite(n) && n >= 0 ? n : 0;
    },
    header: () => null,
    enableSorting: true,
    enableGlobalFilter: false,
    meta: { filterOnly: true },
  },
  {
    id: 'prAdditions',
    accessorFn: (row) => {
      const n = Number(row.additions);
      return Number.isFinite(n) && n >= 0 ? n : 0;
    },
    header: () => null,
    enableSorting: true,
    enableGlobalFilter: false,
    meta: { filterOnly: true },
  },
  {
    id: 'prDeletions',
    accessorFn: (row) => {
      const n = Number(row.deletions);
      return Number.isFinite(n) && n >= 0 ? n : 0;
    },
    header: () => null,
    enableSorting: true,
    enableGlobalFilter: false,
    meta: { filterOnly: true },
  },
  {
    id: 'pr',
    header: 'Pull request',
    enableGlobalFilter: false,
    accessorFn: (row) => (row.createdAt ? new Date(row.createdAt).getTime() : 0),
    meta: {
      tdClassName:
        'px-4 py-3 text-sm text-slate-200 max-w-xl min-w-[12rem] whitespace-normal align-top',
    },
    cell: ({ row }) => {
      const pr = row.original;
      const title = pr.title || pr.name || 'Untitled';
      const num = pr.number != null ? String(pr.number) : '';
      const author = pr.authorName || pr.author || 'Unknown';
      const statusRaw =
        (pr.status && String(pr.status).trim()) || String(pr.state || 'open').toLowerCase();
      const statusLabel = statusRaw.charAt(0).toUpperCase() + statusRaw.slice(1).toLowerCase();
      const dateStr = formatRepoTabDate(pr.createdAt);
      const labels = normalisePrLabels(pr.labels);
      const assignees = Array.isArray(pr.assignees)
        ? pr.assignees.map((a) => String(a).trim()).filter(Boolean)
        : [];
      const reviewers = Array.isArray(pr.requestedReviewers)
        ? pr.requestedReviewers.map((r0) => String(r0).trim()).filter(Boolean)
        : [];
      const isDraft = Boolean(pr.draft);
      const commitCountN = Number(pr.commitCount);
      const hasCommitCount = Number.isFinite(commitCountN) && commitCountN >= 0;
      const mergeShaRaw = pr.mergeCommitSha != null ? String(pr.mergeCommitSha).trim() : '';
      const showMergeSha = Boolean(pr.merged) && mergeShaRaw.length > 0;
      const mergeShort = mergeShaRaw.length >= 7 ? mergeShaRaw.slice(0, 7) : mergeShaRaw;
      const repoFull = pr.repository && String(pr.repository).trim();
      const mergeCommitUrl =
        showMergeSha && repoFull && /^[^/\s]+\/[^/\s]+$/.test(repoFull)
          ? `https://github.com/${repoFull}/commit/${mergeShaRaw}`
          : null;
      const hasMetaChips =
        isDraft || labels.length > 0 || assignees.length > 0 || reviewers.length > 0;
      const maxLabels = 8;
      const shownLabels = labels.slice(0, maxLabels);
      const moreLabels = labels.length - shownLabels.length;
      const maxPeople = 5;
      const shownAssignees = assignees.slice(0, maxPeople);
      const moreAssignees = assignees.length - shownAssignees.length;
      const shownReviewers = reviewers.slice(0, maxPeople);
      const moreReviewers = reviewers.length - shownReviewers.length;
      const personChip =
        'inline-flex max-w-[7.5rem] shrink-0 truncate rounded-md border border-dark-border bg-dark-elevated px-1.5 py-0.5 text-[11px] font-medium text-slate-200';
      const numLinkClass = `${commitHashClass} hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple/50`;
      const titleLinkClass = `${prTitleClass} inline-flex items-center gap-1 leading-snug focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple/50 rounded-sm`;
      const numInner = num ? `#${num}` : '—';
      return (
        <div className="flex gap-3 items-start min-w-0">
          <div className="flex flex-col gap-1.5 shrink-0 items-center">
            <PrStatusIcon pr={pr} />
            {pr.htmlUrl && num ? (
              <a
                href={pr.htmlUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={numLinkClass}
                aria-label={`View pull request ${num} on GitHub`}
              >
                {numInner}
              </a>
            ) : (
              <span className={commitHashClass} title={num ? undefined : 'No PR number'}>
                {numInner}
              </span>
            )}
          </div>
          <div className="space-y-1.5 min-w-0 flex-1">
            {pr.htmlUrl ? (
              <a
                href={pr.htmlUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={titleLinkClass}
              >
                <span className="line-clamp-2">{title}</span>
                <ExternalLink className="w-3.5 h-3.5 shrink-0 opacity-70" aria-hidden />
              </a>
            ) : (
              <div className={`${prTitleClass} leading-snug line-clamp-2`}>{title}</div>
            )}
            <div className={`${prMetaClass} flex flex-wrap items-center gap-x-2 gap-y-1`}>
              <span className="inline-flex min-w-0 max-w-full items-center gap-1.5">
                <ContributorRowAvatar
                  row={{ ...pr, displayName: author }}
                  size="xxs"
                  className="shrink-0"
                />
                <span className="min-w-0 truncate">{author}</span>
              </span>
              <span className="text-slate-600" aria-hidden>
                ·
              </span>
              <span className="capitalize">{statusLabel}</span>
              <span className="text-slate-600" aria-hidden>
                ·
              </span>
              <span className="shrink-0 whitespace-nowrap">{dateStr}</span>
              {hasCommitCount ? (
                <>
                  <span className="text-slate-600" aria-hidden>
                    ·
                  </span>
                  <span
                    className="shrink-0 whitespace-nowrap tabular-nums"
                    title={`${commitCountN} commits`}
                  >
                    {commitCountN} commit{commitCountN === 1 ? '' : 's'}
                  </span>
                </>
              ) : null}
              {showMergeSha ? (
                <>
                  <span className="text-slate-600" aria-hidden>
                    ·
                  </span>
                  {mergeCommitUrl ? (
                    <a
                      href={mergeCommitUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`${commitHashClass} hover:opacity-90`}
                      title={mergeShaRaw}
                    >
                      {mergeShort}
                    </a>
                  ) : (
                    <span className={commitHashClass} title={mergeShaRaw}>
                      {mergeShort}
                    </span>
                  )}
                </>
              ) : null}
              {hasMetaChips ? (
                <>
                  <span className="text-slate-600" aria-hidden>
                    ·
                  </span>
                  <div
                    className="flex min-w-0 flex-[1_1_8rem] flex-wrap items-center gap-1"
                    role="group"
                    aria-label="Pull request labels, assignees, and reviewers"
                  >
                    {isDraft ? <Badge variant="warning">Draft</Badge> : null}
                    {shownLabels.map((label, i) => (
                      <span
                        key={`${label}-${i}`}
                        title={label}
                        className="inline-flex max-w-[10rem] shrink-0 truncate rounded-full bg-slate-400 px-2 py-0.5 text-dark-elevated font-medium ring-slate-500/60"
                      >
                        {label}
                      </span>
                    ))}
                    {moreLabels > 0 ? (
                      <span className="text-[11px] font-medium text-slate-400">+{moreLabels}</span>
                    ) : null}
                    {assignees.length > 0 ? (
                      <span className="inline-flex max-w-full flex-wrap items-center gap-0.5">
                        <Users className="h-3 w-3 shrink-0 text-slate-500" aria-hidden />
                        {shownAssignees.map((login) => (
                          <span
                            key={`a-${login}`}
                            className={personChip}
                            title={`Assignee @${login}`}
                          >
                            @{login}
                          </span>
                        ))}
                        {moreAssignees > 0 ? (
                          <span className="text-[11px] font-medium text-slate-400">
                            +{moreAssignees}
                          </span>
                        ) : null}
                      </span>
                    ) : null}
                    {reviewers.length > 0 ? (
                      <span className="inline-flex max-w-full flex-wrap items-center gap-0.5">
                        <Eye className="h-3 w-3 shrink-0 text-slate-500" aria-hidden />
                        {shownReviewers.map((login) => (
                          <span
                            key={`r-${login}`}
                            className={personChip}
                            title={`Review requested from @${login}`}
                          >
                            @{login}
                          </span>
                        ))}
                        {moreReviewers > 0 ? (
                          <span className="text-[11px] font-medium text-slate-400">
                            +{moreReviewers}
                          </span>
                        ) : null}
                      </span>
                    ) : null}
                  </div>
                </>
              ) : null}
            </div>
          </div>
        </div>
      );
    },
  },
  {
    id: 'prBranches',
    header: 'Branches',
    enableGlobalFilter: false,
    enableSorting: false,
    accessorFn: (row) =>
      [row.headBranch, row.baseBranch].filter((b) => b != null && String(b).trim()).join(' ') || '',
    meta: {
      tdClassName:
        'px-4 py-3 text-sm align-top text-slate-100 w-[min(22rem,40vw)] min-w-[10rem] max-w-md whitespace-normal',
    },
    cell: ({ row }) => {
      const pr = row.original;
      const repo = pr.repository && String(pr.repository).trim();
      const head = pr.headBranch != null ? String(pr.headBranch).trim() : '';
      const base = pr.baseBranch != null ? String(pr.baseBranch).trim() : '';
      const headUrl = githubRepoBranchUrl(repo, head);
      const baseUrl = githubRepoBranchUrl(repo, base);
      const branchRowClass =
        'inline-flex min-w-0 max-w-full items-center gap-1.5 text-xs font-mono tabular-nums text-slate-200';
      const branchLinkClass = `${branchRowClass} rounded-md hover:text-red-400 focus:outline-none focus-visible:ring-offset-dark-base`;
      const branchMutedClass = `${branchRowClass} text-slate-500`;

      const branchLine = (label, url) => {
        if (!label) {
          return <span className={branchMutedClass}>—</span>;
        }
        const inner = (
          <>
            <GitBranch className="h-3.5 w-3.5 shrink-0 text-slate-500" aria-hidden />
            <span className="min-w-0 truncate" title={label}>
              {label}
            </span>
          </>
        );
        if (url) {
          return (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className={branchLinkClass}
              aria-label={`View branch ${label} on GitHub`}
            >
              {inner}
            </a>
          );
        }
        return <span className={branchMutedClass}>{inner}</span>;
      };

      if (!head && !base) {
        return <span className="text-sm text-slate-500">—</span>;
      }

      const showArrow = Boolean(head && base);

      return (
        <div
          className="flex flex-wrap items-center gap-x-2 gap-y-1.5"
          aria-label={
            head && base
              ? `Branches: ${head} into ${base}`
              : head
                ? `Head branch: ${head}`
                : `Base branch: ${base}`
          }
        >
          {branchLine(head, headUrl)}
          {showArrow ? (
            <ArrowRight className="h-3.5 w-3.5 shrink-0 text-slate-500" aria-hidden />
          ) : null}
          {branchLine(base, baseUrl)}
        </div>
      );
    },
  },
  makeDonutColumn('pr'),
  makeChurnColumn('pr'),
];
